/* eslint-disable prettier/prettier */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { Link } from 'react-router-dom';
import { Navbar, Nav, NavDropdown, Button } from 'react-bootstrap';
import { FaCog, FaEnvelope, FaBarcode, FaSearch } from 'react-icons/fa';
import { FiUser, FiLogOut } from 'react-icons/fi';

import { addYears, format, parseISO } from 'date-fns';
import { FormHandles } from '@unform/core';
import { Form } from '@unform/web';

import { startOfToday } from 'date-fns/esm';
import { useAuth } from '../../hooks/auth';
import { useToast } from '../../hooks/toast';
import { useClinica } from '../../hooks/clinica';
import api from '../../services/api';

import { Logo, Profile, Icones, DivFormBusca } from './style';

import Input from '../Input';
import Modal from '../Modal';
import Chat, { ISendFormatada } from '../Chat';
import { IMessage, IUsuario, useChat } from '../../hooks/chat';
import socket from '../../services/socket';
import { TIPO_USUARIO } from '../../utils/funcoes';

interface IFormBusca {
  PacNome: string;
}

const Header: React.FC = () => {
  const { user, clinica } = useAuth();
  const { imagensClinica } = useClinica();
  const {
    naoLidas,
    usuarioSelecionado,
    addMensagem,
    addMensagens,
    setUsuarioSelecionado,
    buscarUsuarios,
    atualizaNaoLidas,
  } = useChat();
  const { addToast } = useToast();
  const formRefBusca = useRef<FormHandles>(null);
  const [showModalMensagens, setShowModalMensagens] = useState(false);
  const [showModalBuscaAgenda, setShowModalBuscaAgenda] = useState(false);
  const [tituloChat, setTituloChat] = useState('Chat');
  const [chamarPaciente, SetChamarPaciente] = useState<IMessage | undefined>(
    undefined,
  );
  const [novaMensagem, SetNovaMensagem] = useState<ISendFormatada | undefined>(
    undefined,
  );

  const logo =
    imagensClinica.find(img => img.nome === 'logo')?.arquivo_url || '';

  useEffect(() => {
    if (user && clinica.CliNomeAbreviado !== 'Agenda Fácil') {
      let sessionId = localStorage.getItem('@AgendaFacil:sessionId');
      if (!sessionId) {
        sessionId = uuidv4();
        localStorage.setItem(
          '@AgendaFacil:sessionId',
          JSON.stringify(sessionId),
        );
      }

      const usuario: IUsuario = {
        clinica: clinica.CliNomeAbreviado,
        socketId: socket.id,
        codClinica: user.UsuClinica.toString(),
        pagina: 'teste',
        sessionId,
        usuCodigo: user.UsuCodigo.toString(),
        usuNome: user.UsuNome,
        usuTipo: user.UsuTipo,
      };
      socket.emit('registro', usuario);

      socket.on('connect', () => {
        socket.emit('registro', usuario);
      });
      socket.on('connect_error', () => console.log('erro de conexão'));
      socket.on('disconnect', (reason: string) =>
        console.log(`desconectado ${reason}`),
      );
      socket.on('desconectar', () => window.location.replace('/sair'));
      buscarUsuarios(user.UsuCodigo);
    }
    return () => {
      socket.removeEventListener('connect');
      socket.removeEventListener('connect_error');
      socket.removeEventListener('disconnect');
      socket.removeEventListener('desconectar');
    };
  }, [buscarUsuarios, clinica.CliNomeAbreviado, user]);

  useEffect(() => {
    if (
      usuarioSelecionado &&
      novaMensagem &&
      usuarioSelecionado.UsuCodigo === novaMensagem.idFrom
    ) {
      const dadosRecebidos: ISendFormatada = {
        ...novaMensagem,
        dataFormatada: novaMensagem.created_at
          ? format(parseISO(novaMensagem.created_at?.toString()), 'dd/MM HH:mm')
          : format(new Date(), 'dd/MM HH:mm'),
      };

      addMensagem(dadosRecebidos);
    } else if (novaMensagem) {
      atualizaNaoLidas(novaMensagem.idFrom);
    }
    SetNovaMensagem(undefined);
  }, [usuarioSelecionado, novaMensagem, addMensagem, atualizaNaoLidas]);

  useEffect(() => {
    const handleNewMessage = (data: ISendFormatada): void => {
      SetNovaMensagem(data);
    };

    socket.on('messages', handleNewMessage);
    return () => {
      socket.removeEventListener('messages', handleNewMessage);
      SetNovaMensagem(undefined);
    };
  }, []);

  useEffect(() => {
    const handleChamarPaciente = (data: IMessage): void => {
      SetChamarPaciente(data);
    };

    socket.on('chamar_paciente', handleChamarPaciente);
    return () => {
      socket.removeEventListener('chamar_paciente', handleChamarPaciente);
    };
  }, []);

  useEffect(() => {
    if (chamarPaciente)
      addToast({
        title: `Dr ${chamarPaciente?.from}`,
        type: 'info',
        description: `${format(new Date(), 'HH:mm:ss')} - ${chamarPaciente?.mensagem
          }`,
        timeAlive: 40 * 60000,
      });
  }, [chamarPaciente, addToast]);

  const handlerFecharChat = useCallback(() => {
    setUsuarioSelecionado(undefined);
    addMensagens(undefined);
    setShowModalMensagens(false);
  }, [setUsuarioSelecionado, addMensagens]);

  const handleSubmitBusca = useCallback((dados: IFormBusca) => {
    const hoje = startOfToday();
    const dataInicioFormatada = format(addYears(hoje, -3), 'yyyy-MM-dd');
    const dataFimFormatada = format(addYears(hoje, 1), 'yyyy-MM-dd');
    window.location.replace(
      `/agendabusca?paciente=${dados.PacNome}&data=${dataInicioFormatada}&datafim=${dataFimFormatada}`,
    );
  }, []);

  const handleLogout = useCallback(async () => {
    try {
      await api.delete(`sessions/${user.UsuCodigo}`);
    } catch (error) {
      console.log('erro:', error);
    } finally {
      window.location.replace('/sair');
    }
  }, [user.UsuCodigo]);

  return (
    <>
      {showModalMensagens && (
        <Modal
          show={showModalMensagens}
          titulo={tituloChat}
          handleFecharModal={handlerFecharChat}
          large
        >
          <Chat setTitulo={setTituloChat} />
        </Modal>
      )}

      {showModalBuscaAgenda && (
        <Modal
          show={showModalBuscaAgenda}
          titulo="Busca agendamentos"
          handleFecharModal={() => setShowModalBuscaAgenda(false)}
        >
          <Form ref={formRefBusca} onSubmit={handleSubmitBusca}>
            <DivFormBusca>
              <Input
                name="PacNome"
                label="Nome"
                maxLength={100}
                minLength={3}
                containerStyle={{ flex: 1 }}
              />

              <Button type="submit" size="sm" variant="outline-secondary">
                <FaSearch />
              </Button>
            </DivFormBusca>
          </Form>
        </Modal>
      )}
      <Navbar expand="lg" variant="dark" className={`py-2 ${clinica.CliCor}`}>
        <Navbar.Brand className="py-0">
          <Logo>
            <Link to="/dashboard">
              <img src={logo} alt={clinica.CliNomeAbreviado} />
            </Link>
          </Logo>
        </Navbar.Brand>
        <Navbar.Toggle aria-controls="basic-navbar-nav" />
        <Navbar.Collapse id="basic-navbar-nav">
          {user.UsuTipo !== TIPO_USUARIO.MEDICO && (
            <Nav className="mr-auto">
              <Navbar.Text className="mr-3">
                <Link to="/agendas">Agenda</Link>
              </Navbar.Text>
              <Navbar.Text className="mr-3">
                <Link to="/paciente">Pacientes</Link>
              </Navbar.Text>
              <Navbar.Text className="mr-3">
                <Link to="/cartaosaude">Cartão Saúde</Link>
              </Navbar.Text>
              <Navbar.Text className="mr-2">
                <Link to="/caixa">Caixa</Link>
              </Navbar.Text>
              <Navbar.Text className="mr-3">
                <Link to="/agendamento/especialidade">
                  Agenda por Especialidade
                </Link>
              </Navbar.Text>
              <Navbar.Text className="mr-3">
                <Link to="/listacheckup">Listagem Checkup</Link>
              </Navbar.Text>
              {user.UsuCodigo === 1 && (
                <Navbar.Text className="mr-2">
                  <Link to="/pacienteunificacao">Unificação</Link>
                </Navbar.Text>
              )}
              <Navbar.Text
                style={{ paddingBottom: '0', paddingTop: '0.25rem' }}
              >
                <Button
                  type="button"
                  variant="outline-light"
                  size="sm"
                  className="py-0 border-0"
                  onClick={() => {
                    setShowModalBuscaAgenda(true);
                  }}
                >
                  <FaSearch />
                </Button>
              </Navbar.Text>
            </Nav>
          )}
          <Nav>
            <span style={{ color: '#fff', fontSize: '1.3rem' }}>
              {clinica.CliNomeAbreviado}
            </span>
          </Nav>
          <Nav className="ml-auto">
            <aside className="d-flex align-items-center">
              <Icones>
                <FaEnvelope
                  onClick={() => {
                    setShowModalMensagens(true);
                    setTituloChat('Chat');
                  }}
                  size={20}
                  color="#fff"
                />
                <span id="qtdmessage">{naoLidas}</span>
                {user.UsuTipo !== TIPO_USUARIO.MEDICO && (
                  <Link
                    to="/configuracao"
                    // className="dropdown-item text-dark"
                    role="button"
                  >
                    <FaCog size={20} color="#fff" />
                  </Link>
                )}
              </Icones>
              <Profile>
                <div>
                  <NavDropdown
                    // eslint-disable-next-line prettier/prettier
                    title={
                      // eslint-disable-next-line react/jsx-wrap-multilines
                      <>
                        <img src={user.avatar_url} alt={user.UsuNome} />
                      </>
                    }
                    id="dropdown-Func"
                    style={{ color: '#464844' }}
                    alignRight
                  >
                    <NavDropdown.ItemText className="p-1">
                      <div className="dropdown-item text-dark">
                        {user.UsuNome}
                      </div>
                    </NavDropdown.ItemText>
                    <NavDropdown.Divider className="m-0" />
                    <NavDropdown.ItemText className="p-1">
                      <div className="dropdown-item text-dark p-2">
                        <FaBarcode size={18} /> Cod. {user.UsuCodigo}
                      </div>
                    </NavDropdown.ItemText>
                    <NavDropdown.ItemText className="p-1">
                      <Link
                        to="/profile"
                        className="dropdown-item text-dark p-2"
                        role="button"
                      >
                        <FiUser size={18} /> Meu Perfil
                      </Link>
                    </NavDropdown.ItemText>

                    <NavDropdown.ItemText className="p-1">
                      <button
                        type="button"
                        onClick={handleLogout}
                        className="dropdown-item text-dark p-2"
                      >
                        <FiLogOut size={18} /> Sair
                      </button>
                    </NavDropdown.ItemText>
                  </NavDropdown>
                </div>
              </Profile>
            </aside>
          </Nav>
        </Navbar.Collapse>
      </Navbar>
    </>
  );
};

export default Header;
